/**
 * Network-adaptive upload configuration.
 *
 * Reads the Network Information API and returns timeouts, retry counts,
 * and concurrency limits tuned to the current connection quality.
 * All values degrade gracefully when the API is unavailable.
 *
 * Quality tiers:
 *   VERY SLOW  — slow-2g / 2g / downlink < 0.08 Mbps
 *   SLOW       — 3g / downlink < 0.7 Mbps
 *   FAST       — 4g / wifi / unknown (assume fast, retry less aggressively)
 */

export function getNavigatorConnection() {
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
}

export function getUploadNetworkProfile(connection = getNavigatorConnection()) {
  if (!connection) {
    return { effectiveType: 'unknown', downlinkMbps: null, isVerySlow: false, isSlow: false };
  }
  const et = connection.effectiveType;
  const dl = connection.downlink ?? null;
  const isVerySlow = et === 'slow-2g' || et === '2g' || (dl !== null && dl < 0.08);
  const isSlow = !isVerySlow && (et === '3g' || (dl !== null && dl < 0.7));
  return { effectiveType: et, downlinkMbps: dl, isVerySlow, isSlow };
}

/** Per-chunk upload timeout (ms). */
export function getChunkTimeoutMs(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow } = getUploadNetworkProfile(connection);
  if (isVerySlow) return 10 * 60 * 1000; // 10 min on 2G
  if (isSlow)    return  6 * 60 * 1000;  // 6 min on 3G
  return                  3 * 60 * 1000; // 3 min on 4G+
}

/** complete-upload finalization timeout (ms). */
export function getCompleteTimeoutMs(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow } = getUploadNetworkProfile(connection);
  if (isVerySlow) return 5 * 60 * 1000;
  if (isSlow)    return 3 * 60 * 1000;
  return                2 * 60 * 1000;
}

/** How long to wait for connectivity to return before giving up on one attempt (ms). */
export function getOfflineWaitMs(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow } = getUploadNetworkProfile(connection);
  if (isVerySlow) return 5 * 60 * 1000; // 2G is intermittent, wait longer
  if (isSlow)    return 2 * 60 * 1000;
  return                    30 * 1000;
}

/** Max retry attempts per chunk. */
export function getUploadRetryCount(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow } = getUploadNetworkProfile(connection);
  if (isVerySlow) return 7;
  if (isSlow)    return 6;
  return                 5;
}

/** Max concurrent chunk uploads. */
export function getMaxConcurrentChunks(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow } = getUploadNetworkProfile(connection);
  if (isVerySlow) return 1;
  if (isSlow)    return 2;
  return                 3;
}

/** Files smaller than this skip compression (already small enough). */
export function getCompressionSkipThresholdBytes(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow } = getUploadNetworkProfile(connection);
  if (isVerySlow) return 1 * 1024 * 1024; // 1 MB
  if (isSlow)    return 2 * 1024 * 1024; // 2 MB
  return                5 * 1024 * 1024; // 5 MB
}

/** Human-readable network quality label. */
export function getNetworkLabel(connection = getNavigatorConnection()) {
  const { isVerySlow, isSlow, effectiveType } = getUploadNetworkProfile(connection);
  if (isVerySlow) return effectiveType?.toUpperCase() || '2G';
  if (isSlow)    return '3G';
  return null; // fast — no label needed
}
